import React, {ReactElement, ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {cx} from '@emotion/css';
import {InformationIcon} from '@fo/root/designSystem/atoms/Icons';
import {IconSize} from '@fo/root/designSystem/atoms/Icons/types';
import Tooltip from '@fo/root/designSystem/atoms/Tooltip';
import ErrorMessage from './ErrorMessage';
import AssistiveText from './AssistiveText';

import styles from './Field.styles';

export interface Props {
  children: ReactNode;
  label?: string;
  name: string;
  assistiveMessage?: ReactNode;
  errorMessage?: ReactNode;
  warningMessage?: ReactNode;
  showOptionalMessage?: boolean;
  isDisabled?: boolean;
  labelClassName?: string;
  className?: string;
  labelTestId?: string;
  tooltip?: string;
}

function Field({
  assistiveMessage = '',
  children,
  errorMessage = '',
  label = '',
  name,
  warningMessage = '',
  showOptionalMessage,
  isDisabled,
  labelClassName,
  className,
  labelTestId,
  tooltip,
}: Props): ReactElement {
  const {formatMessage} = useIntl();
  const hasWarning = !!warningMessage;
  const hasError = !!errorMessage;

  return (
    <div className={cx(styles.field(hasWarning, hasError), className)}>
      {label ? (
        <label
          htmlFor={name}
          className={cx(styles.label(isDisabled), labelClassName)}
          data-testid={labelTestId || `ui.form.field.label.${name}`}
          data-disabled={isDisabled}
          data-has-warning={hasWarning}
          data-has-error={hasError}
        >
          {label}
          {showOptionalMessage && (
            <span data-testid={`ui.form.field.optional_message.${name}`} className={cx(styles.optionalMessage)}>
              &nbsp;{formatMessage({id: 'phoenix.fields.optional'})}
            </span>
          )}
          {tooltip && (
            <Tooltip title={tooltip} className={cx(styles.tooltip)}>
              <InformationIcon size={IconSize.EXTRA_SMALL} />
            </Tooltip>
          )}
        </label>
      ) : null}
      {assistiveMessage ? <AssistiveText isDisabled={isDisabled} assistiveMessage={assistiveMessage} /> : null}
      {errorMessage || warningMessage ? (
        <ErrorMessage
          testId={`ui.form.field.error.${name}`}
          hasWarning={hasWarning}
          hasError={hasError}
          errorMessage={errorMessage}
          warningMessage={warningMessage}
        />
      ) : null}
      {children}
    </div>
  );
}

export default Field;
